// src/components/store/sections/deal/index.tsx
"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { Flame, ArrowRight, Clock } from "lucide-react";
import Variant1Spotlight from "./Variant1Spotlight";
import Variant2PastelCarousel from "./Variant2PastelCarousel";
import Variant3FlashGrid from "./Variant3FlashGrid";
import Variant4PastelGrid from "./Variant4PastelGrid";
import {
  PRODUCT_CARD_COMPONENTS,
  resolveProductCardComponent,
} from "../product-cards";

export const DEAL_VARIANTS: Record<string, React.ComponentType<any>> = {
  timer_urgency: Variant1Spotlight,
  spotlight_dashboard: Variant1Spotlight,
  pastel_carousel: Variant2PastelCarousel,
  flash_sale: Variant2PastelCarousel,
  flash_grid: Variant3FlashGrid,
  banner_countdown: Variant3FlashGrid,
  pastel_grid: Variant4PastelGrid,
  variant_4: Variant4PastelGrid,
};

interface DealSectionProps {
  products?: any[];
  product?: any;
  subdomain?: string;
  variant?: string;
}

export default function DealSection({
  products = [],
  product,
  subdomain = "nextlook",
  variant = "pastel_grid",
}: DealSectionProps) {
  const [activeVariant, setActiveVariant] = useState<string>(variant || "pastel_grid");

  useEffect(() => {
    const syncDesign = () => {
      if (variant) setActiveVariant(variant);
      if (typeof window !== "undefined") {
        try {
          const rawSectionDesigns = localStorage.getItem("section_designs");
          if (rawSectionDesigns) {
            const parsed = JSON.parse(rawSectionDesigns);
            if (parsed.deals_of_the_day) {
              setActiveVariant(parsed.deals_of_the_day);
            }
          }
        } catch {}
      }
    };
    syncDesign();
    if (typeof window !== "undefined") {
      window.addEventListener("storage", syncDesign);
      window.addEventListener("theme_settings_updated", syncDesign);
      return () => {
        window.removeEventListener("storage", syncDesign);
        window.removeEventListener("theme_settings_updated", syncDesign);
      };
    }
  }, [variant]);

  const dealProducts = products && products.length > 0 ? products : product ? [product] : [];
  if (dealProducts.length === 0) return null;

  const cleanKey = String(activeVariant || "pastel_grid").trim().toLowerCase().replace(/-/g, "_");

  if (DEAL_VARIANTS[cleanKey]) {
    const DedicatedDealSection = DEAL_VARIANTS[cleanKey];
    return <DedicatedDealSection products={dealProducts} subdomain={subdomain} />;
  }

  const CardComponent = resolveProductCardComponent(cleanKey);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 font-sans select-none space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-b border-slate-100 pb-4">
        <div className="space-y-1.5">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-50 text-rose-600 border border-rose-200 text-xs font-black">
            <Flame className="w-3.5 h-3.5 fill-rose-600" />
            <span>Limited Time Deals</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
            Deals of the Day ({dealProducts.length})
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 font-medium">
            Grab your favorite products at special flash discount prices before time runs out!
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-rose-50 border border-rose-200 text-xs font-mono font-black text-rose-600">
            <Clock className="w-3.5 h-3.5 animate-pulse" />
            <span>Flash Offer Live</span>
          </div>
          <Link
            href="/products"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs shadow-md transition-all active:scale-95"
          >
            <span>View All Deals</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3.5 sm:gap-6 items-stretch">
        {dealProducts.map((item: any, idx: number) => (
          <div key={item.id || idx} className="h-full flex">
            <CardComponent
              product={item}
              index={idx}
              subdomain={subdomain}
              showTimer={true}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

export {
  Variant1Spotlight,
  Variant2PastelCarousel,
  Variant3FlashGrid,
  Variant4PastelGrid,
  PRODUCT_CARD_COMPONENTS,
};
